import { Component } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import lottie from 'lottie-web';
import { defineElement } from '@lordicon/element';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

defineElement(lottie.loadAnimation);
import { CognitoService } from 'src/app/core/service/cognito.service';
import { TablesService } from 'src/app/core/service/table.service';
import { ToastService } from 'src/app/toast/toast.service';

import {
  SidebarToggleObservable,
  CreditCountObservable,
  UserInfoObservable,
} from 'src/app/core/service/observable.service';
import { UserInfo } from 'src/app/core/service/user.service';
import { AuthStorageService } from 'src/app/core/service/auth-storage.service';
import { LocalStorageService } from 'src/app/core/service/local-storage.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent {
  subscription: Subscription = new Subscription();
  creditCountSubscription: Subscription = new Subscription();
  userInfoSubscription: Subscription = new Subscription();
  sidebarToggleSubscription: Subscription = new Subscription();
  dataset: any;
  activeTab = 'all'; // Default active tab
  hideSideBarChevron: boolean = false;
  _creditCount: number = 0;
  creditCountLevel: 'none' | 'low' | 'medium' | 'high' = 'none';
  _userInfo: UserInfo = {} as UserInfo;
  sidebarToggled: boolean = false;

  get isFreemiumUser(): boolean {
    return this._userInfo.subscriptionTier === 't2'; // t2 is FREEMIUM tier
  }

  get creditCount() {
    return this._creditCount;
  }
  set creditCount(value: number) {
    this._creditCount = value;
    this.creditCountLevel = value > 5 ? 'high' : value > 2 ? 'low' : 'none';
  }

  get userInfo() {
    return this._userInfo;
  }
  set userInfo(value: UserInfo) {
    this._userInfo = value;
  }

  constructor(
    public cognitoService: CognitoService,
    private router: Router,
    private tableService: TablesService,
    private modalService: NgbModal,
    private toastService: ToastService,
    private sidebarToggleObservable: SidebarToggleObservable,
    private creditCountObservable: CreditCountObservable,
    private userInfoObservable: UserInfoObservable,
    private authStorage: AuthStorageService,
    private localStorageService: LocalStorageService
  ) {}
  ngOnInit() {
    this.subscription = this.tableService.getData().subscribe((data: any) => {
      this.dataset = data;
    });

    this.subscription = this.tableService
      .getUpdateFromSpreadsheet()
      .subscribe((data: any) => {
        this.dataset = data;
      });

    this.creditCountSubscription = this.creditCountObservable
      .updates()
      .subscribe((data: number) => {
        this.creditCount = data;
        if (this.userInfo) {
          this.userInfo.credits = data;
        }
      });

    this.userInfoSubscription = this.userInfoObservable
      .updates()
      .subscribe((data: UserInfo) => {
        console.log('HeaderComponent:userInfoSubscription', data);
        this.userInfo = data;
      });

    this.sidebarToggleSubscription =
      this.sidebarToggleObservable.toggled$.subscribe((toggled: boolean) => {
        this.sidebarToggled = toggled;
      });

    this.initializeSidebarState();
    this.creditCount = this.creditCountObservable.get();
    this.userInfo = this.userInfoObservable.get();
  }
  logout() {
    this.cognitoService.signOut().then((res: any) => {
      this.authStorage.clearAuthData();
      window.localStorage.clear();
      window.location.href = '/auth/signIn';
    });
  }

  editTableName(table: any) {
    table.tableName = this.dataset.tableName;
    table.editedSheetName = table.tableName;
    table.isSheetNameEdit = true;
    return;
  }

  duplicateTable(table: any) {
    this.tableService
      .duplicateTable(this.dataset.tableId, table)
      .subscribe((res: any) => {
        this.toastService.show('info', 'Table is duplicated successfully.', {
          classname:
            'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
        });
        this.tableService.setUpdateNotification(res);
      });
  }
  deleteId: any;
  confirm(content: any, id: any) {
    this.deleteId = id;
    this.modalService.open(content, { centered: true });
  }

  deleteItem(deleteId: any) {
    this.tableService.deleteTable(this.deleteId).subscribe((res: any) => {
      this.localStorageService.clearInterruptedUploadForTable(this.deleteId);
      // setting blank
      this.dataset.tableName = '';
      this.tableService.setUpdateNotification(this.dataset);
      this.toastService.show('info', 'Table is deleted.', {
        classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
      });
    });
  }

  stopEditing(table: any) {
    table.tableName = table.editedSheetName;
    this.tableService
      .updateTable(table.tableId, { tableName: table.editedSheetName })
      .subscribe((res: any) => {
        table.isSheetNameEdit = false;
        this.toastService.show('info', 'Table Name updated', {
          classname:
            'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
        });
        this.tableService.setUpdateNotification(table);
      });
  }

  setActiveTab(tabName: string) {
    this.activeTab = tabName;
  }

  toggleSidebar() {
    this.sidebarToggleObservable.toggleSidebar();
  }

  getUserInitial() {
    return this.userInfo?.firstName?.toUpperCase()[0] || ' '; // <- that's a non-breaking space
  }

  openModal(content: any) {
    this.modalService.open(content, { centered: true });
  }

  initializeSidebarState() {
    const savedExpanded = localStorage.getItem('sidebarExpanded');
    if (savedExpanded !== null) {
      this.sidebarToggled = JSON.parse(savedExpanded);
      console.log('🔍 HEADER DEBUG - initialized sidebar state from localStorage:', this.sidebarToggled);
    } else {
      // Default to true (expanded) if no saved state
      this.sidebarToggled = true;
      console.log('🔍 HEADER DEBUG - initialized sidebar state to default (true)');
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.creditCountSubscription.unsubscribe();
    this.userInfoSubscription.unsubscribe();
    this.sidebarToggleSubscription.unsubscribe();
  }
}
